import React, { useContext, useEffect, useRef, useState } from "react";
import {
    deleteChat,
    listMessageSuport,
    listUserMessageSuport,
    markChatAsResolved,
    sendMessageSupport,
} from "../../api/context/support";
import moment from "moment/moment";
import { RiMore2Fill } from "react-icons/ri";
import toast from "react-hot-toast";
import { useLocation } from "react-router-dom";
import { SelectionContext } from "../../Context/Store";

 
 const useOutsideClick = (ref, callback) => {
        useEffect(() => {
            const handleClickOutside = (event) => {
                if (ref.current && !ref.current.contains(event.target)) {
                    callback();
                }
            };

            document.addEventListener("mousedown", handleClickOutside);
            return () => {
                document.removeEventListener("mousedown", handleClickOutside);
            };
        }, [ref, callback]);
    };

const ChatSupport = () => {

 
    const location = useLocation();
    const { chatId } = location.state || {};

    const [showOptionsMenu, setShowOptionsMenu] = useState(false);
    const optionsMenuRef = useRef(null);
    const fileInputRef = useRef(null);
    const messagesEndRef = useRef(null);

    const { messages, setMessages, messageUpdate, setMessageUpdate } =
        useContext(SelectionContext);

    const [users, setUsers] = useState([]);
    const [selectedChatRoom, setSelectedChatRoom] = useState(null);
    const [newMessage, setNewMessage] = useState("");
    const [attachment, setAttachment] = useState(null);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [loading, setLoading] = useState(false);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [hasMoreMessages, setHasMoreMessages] = useState(true);

    // Fetch the user list and messages for the selected user
    useEffect(() => {
        fetchUserList();
    }, []);

 
    const fetchUserList = () => {
        listUserMessageSuport()((response) => {
            if (response?.status) {
                setUsers(response?.data?.data);
                // If chatId is provided, find and select the corresponding chat room
                if (chatId) {
                    const chatRoom = response?.data?.data.find(
                        (room) => room.chatRoomId === chatId
                    );
                    if (chatRoom) {
                        setSelectedChatRoom(chatRoom);
                        fetchMessages(chatRoom, true);
                    }
                }
            } else {
                setUsers([]);
            }
        });
    };

          // Add this to close the menu when clicking outside
    useOutsideClick(optionsMenuRef, () => {
        setShowOptionsMenu(false);
    });

    const fetchMessages = (chatRoom, reset = false) => {
        if (!chatRoom) return;

        setLoading(true);
        if (reset) {
            setPage(1);
            setMessages([]);
        }

        listMessageSuport({ chatRoomId: chatRoom?.chatRoomId, page })((response) => {
            if (response?.data?.status) {
                const newMessages = response?.data?.data;
                setMessages((prevMessages) =>
                    reset ? newMessages : [...newMessages, ...prevMessages]
                );
                scrollToBottom(newMessages);
                setHasMore(newMessages.length > 0);
            } else {
                setMessages([]);
                setHasMore(false);
            }
            setLoading(false);
        });
    };

    useEffect(() => {
        if (messageUpdate) {
            if (selectedChatRoom) {
                fetchMessages(selectedChatRoom, true);
            }
            setMessageUpdate(false);
        }
    }, [messageUpdate]);

    const scrollToBottom = (newMessages) => {
        const firstMessageId = newMessages?.[0]?.id;
        if (firstMessageId) {
            setTimeout(() => {
                const element = document.getElementById(firstMessageId);
                if (element) {
                    element.scrollIntoView();
                }
            }, 500);
        }
    };

    const handleMarkAsResolved = () => {
        if (!selectedChatRoom) return;

        setShowOptionsMenu(false);
        markChatAsResolved({ chatRoomId: selectedChatRoom.chatRoomId })((response) => {
            if (response.data?.status) {
                toast.success("Chat marked as resolved successfully");
                // Update the chat room status in the users list
                setUsers(prevUsers =>
                    prevUsers.map(room =>
                        room.chatRoomId === selectedChatRoom.chatRoomId
                            ? { ...room, isResolved: true }
                            : room
                    )
                );
                // Update the selected chat room status
                setSelectedChatRoom(prev => ({ ...prev, isResolved: true }));
            } else {
                toast.error(response.data?.error || "Failed to mark chat as resolved");
            }
        });
    };

    const handleDeleteChat = () => {
        if (!selectedChatRoom) return;

        setShowOptionsMenu(false);
        deleteChat({ chatRoomId: selectedChatRoom.chatRoomId })((response) => {
            if (response.data?.status) {
                toast.success("Chat deleted successfully");
                // Remove the chat room from the list
                setUsers(prevUsers =>
                    prevUsers.filter(room => room.chatRoomId !== selectedChatRoom.chatRoomId)
                );
                // Clear the current chat
                setSelectedChatRoom(null);
                setMessages([]);
            } else {
                toast.error(response.data?.error || "Failed to delete chat");
            }
        });
    };

    const handleImageChange = (e) => {
        const file = e?.target?.files[0];
        const maxSizeInBytes = 2 * 1024 * 1024; // 2 MB
        const validImageTypes = ["image/jpeg", "image/png", "image/gif"];

        if (file?.size > maxSizeInBytes) {
            toast.error("File size exceeds 2 MB limit.");
            return;
        }

        if (!validImageTypes.includes(file?.type)) {
            toast.error("Only jpg/jpeg, png, and gif files are allowed.");
            return;
        }

        const reader = new FileReader();
        reader.onload = () => {
            setAttachment(reader.result);
        };
        reader.readAsDataURL(file);
    };

    const sendMessage = () => {
        if (!selectedChatRoom) return;
        if (!newMessage.trim() && !attachment) return;

        const payload = {
            chatRoomId: selectedChatRoom.chatRoomId,
            message: newMessage,
            image: attachment,
            senderType: "admin",
        };

        sendMessageSupport(payload)((response) => {
            if (response?.status) {
                setMessages((prevMessages) => [
                    {
                        id: response?.data?.id,
                        chatRoomId: selectedChatRoom.chatRoomId,
                        message: payload.message,
                        image: payload.image,
                        senderType: "admin",
                        createdAt: new Date().toISOString(),
                    },
                    ...prevMessages,
                ]);
                scrollToBottomSend();
                setNewMessage("");
                setAttachment(null);
                if (fileInputRef.current) {
                    fileInputRef.current.value = "";
                }
            } else {
                toast.error("Failed to send message");
            }
        });
    };

    const handleUserClick = (chatRoom) => {
        setSelectedChatRoom(chatRoom);
        setMessages([]);
        setPage(1);
        setCurrentPage(1);
        setHasMoreMessages(true);
        setIsLoadingMore(false);
        fetchMessages(chatRoom, true);
    };

    const handleScroll = () => {
        const container = document.querySelector(".chat-messages");

        if (
            container?.scrollTop === 0 &&
            hasMoreMessages &&
            !isLoadingMore &&
            selectedChatRoom
        ) {
            setIsLoadingMore(true);
            const nextPage = currentPage + 1;

            listMessageSuport({
                chatRoomId: selectedChatRoom.chatRoomId,
                page: nextPage,
            })((response) => {
                if (response?.data?.status && response?.data?.data?.length > 0) {
                    let currentScrollId = null;
                    setMessages((prev) => {
                        const newMessages = [...prev, ...response.data.data];
                        currentScrollId = prev[prev.length - 1];
                        return newMessages;
                    });
                    scrollToBottomMessage(currentScrollId);
                    setCurrentPage(nextPage);
                    setHasMoreMessages(response.data.data.length > 0);
                } else {
                    setHasMoreMessages(false);
                }
                setIsLoadingMore(false);
            });
        }
    };

   

    const scrollToBottomMessage = (newMessages) => {
        const lastMessageId = newMessages?.id;
        if (lastMessageId) {
            const element = document.getElementById(lastMessageId);
            if (element) {
                setTimeout(() => {
                    element.scrollIntoView();
                }, 500);
            }
        }
    };

    const scrollToBottomSend = () => {
        const scrollableDiv = document.querySelector(".chat-messages");
        if (scrollableDiv) {
            scrollableDiv.scrollTop = scrollableDiv.scrollHeight;
        }
    };

    useEffect(() => {
        if (messages.length > 0) {
            scrollToBottomSend();
        }
    }, [messages]);

    return (
        <div>
            <div className="row">
                <div className="col-12">
                    <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                        <h4 className="mb-sm-0">Chat Support</h4>
                    </div>
                </div>
            </div>
            <div className="chat-container">
                {/* User List */}
                <div className="user-list">
                    <h3>Users</h3>
                    {users?.map((chatRoom) => (
                        <div
                            key={chatRoom.chatRoomId}
                            className={`user-item ${selectedChatRoom?.chatRoomId === chatRoom.chatRoomId
                                ? "selected"
                                : ""
                                }`}
                            onClick={() => handleUserClick(chatRoom)}
                        >
                            <div className="user-item-content">
                                {chatRoom.user.firstName || 'user'}
                                {/* {selectedChatRoom.isResolved && (
                                    <RiVerifiedBadgeFill className="resolved-icon-header" />
                                )} */}
                            </div>
                        </div>
                    ))}
                </div>

                {/* Chat Area */}
                <div className="chat-area">
                    {selectedChatRoom ? (
                        <>
                            <div className="support-chat-header">
                                <div className="support-chat-header-content">
                                    <div className="user-name-with-status">
                                        <span className="user-name">{selectedChatRoom.user.firstName || 'user'}</span>
                                        {/* <RiPhoneLine className="call-icon" /> */}
                                        <span className="user-mobile">{selectedChatRoom.user.mobile || ''}</span>

                                    </div>
                                    <div className="support-chat-options" ref={optionsMenuRef}>
                                        <RiMore2Fill
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setShowOptionsMenu(!showOptionsMenu);
                                            }}
                                            className="support-chat-options-icon"
                                        />
                                        {showOptionsMenu && (
                                            <div className="support-chat-options-dropdown">
                                                {!selectedChatRoom.isResolved && (
                                                    <div
                                                        className="support-chat-options-item"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleMarkAsResolved();
                                                        }}
                                                    >
                                                        Mark as resolved
                                                    </div>
                                                )}
                                                <div
                                                    className="support-chat-options-item"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleDeleteChat();
                                                    }}
                                                >
                                                    Delete chat
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div
                                className="chat-messages"
                                onScroll={handleScroll}
                                style={{ overflowY: "auto", height: "400px" }}
                            >
                                {[...messages].reverse().map((msg, index) => (
                                    <div
                                        id={msg?.id}
                                        key={msg.id}
                                        className={`message ${msg.senderType === "admin"
                                            ? "sent customer-support"
                                            : msg.senderType === "customerSupport"
                                                ? "sent customer-support"
                                                : "received"
                                            }`}
                                    >
                                        {msg.image && <img src={msg.image} alt="Attachment" />}

                                        {msg?.message?.split("\n").map((line, index) => (
                                            <React.Fragment key={index}>
                                                {line}
                                                <br />
                                            </React.Fragment>
                                        ))}
                                        <div className="message-time">
                                            {moment(msg?.createdAt).format("hh:mm A")}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {selectedChatRoom.isResolved ? (
                                <div className="resolved-chat-notice">
                                    This chat has been resolved
                                </div>
                            ) : (
                                <div className="chat-input-area">
                                    <textarea
                                        value={newMessage}
                                        onChange={(e) => setNewMessage(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter" && !e.shiftKey) {
                                                e.preventDefault();
                                                sendMessage();
                                            }
                                        }}
                                        placeholder="Type a message"
                                        rows={1}
                                        className="message-textarea"
                                    />
                                    <input
                                        type="file"
                                        accept="image/*"
                                        ref={fileInputRef}
                                        onChange={handleImageChange}
                                        className="max_width"
                                    />
                                    <button onClick={sendMessage}>Send</button>
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="chat-placeholder">Select a user to start chatting</div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ChatSupport;